import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { useTranslation } from '../../../../../../contexts/I18nProvider';

const MAX_LISTED_BINS = 10;

interface ReceiptBinWarningPanelProps {
  /** Mottakslokasjoner som blir plukkbare/reserverbare. Tom = vis kun forklaringen. */
  binCodes?: string[];
}

/**
 * Advarsel når en mottakslokasjon (Varemottak, StcBinCatNo = 2) lagres som plukkbar eller
 * reserverbar (TW 36401238). Brukes både frittstående i ReceiptBinWarningModal og inne i
 * bekreftelsesdialogen til masseoppdateringen.
 */
export const ReceiptBinWarningPanel: React.FC<ReceiptBinWarningPanelProps> = ({ binCodes = [] }) => {
  const { t } = useTranslation();
  const prefix = 'manager.modules.adminItemsLocations.receiptBinWarning';

  return (
    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
      <div className="flex items-start gap-2">
        <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1 text-sm text-yellow-800 space-y-2">
          <p className="font-medium text-yellow-900">
            {t(`${prefix}.title`, {}, 'Mottakslokasjon settes som plukkbar eller reserverbar')}
          </p>
          <p>
            {t(`${prefix}.intro`, {}, 'Mottak er et mellomlager for varer som ikke er lagt ut, ikke en plukklokasjon. Kombinasjonen gir følgende konsekvenser:')}
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>{t(`${prefix}.counting`, {}, 'Telling: saldo på mottak tas ikke med i telleresultatet til ERP. Telles varen på en plukklokasjon, trekker ERP ut det som står på mottak.')}</li>
            <li>{t(`${prefix}.picking`, {}, 'Plukk: plukkgenereringen reserverer og plukker fra mottak, og varene blir stående der i stedet for å bli lagt ut.')}</li>
            <li>{t(`${prefix}.replenishment`, {}, 'Påfylling: saldo på mottak påvirker påfyllingsgenereringen.')}</li>
          </ul>
          {binCodes.length > 0 && (
            <div>
              <p className="font-medium">
                {t(`${prefix}.affectedBins`, { '0': binCodes.length }, 'Gjelder {0} lokasjon(er):')}
              </p>
              <p className="font-mono text-xs mt-1 break-words">
                {binCodes.slice(0, MAX_LISTED_BINS).join(', ')}
                {binCodes.length > MAX_LISTED_BINS && (
                  <span className="italic">
                    {' '}{t(`${prefix}.andMore`, { '0': binCodes.length - MAX_LISTED_BINS }, '... og {0} flere')}
                  </span>
                )}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

interface ReceiptBinWarningModalProps {
  isOpen: boolean;
  binCodes?: string[];
  onConfirm: () => void;
  onCancel: () => void;
}

/** Bekreftelse før lagring — brukeren kan avbryte eller lagre likevel. */
export const ReceiptBinWarningModal: React.FC<ReceiptBinWarningModalProps> = ({
  isOpen,
  binCodes,
  onConfirm,
  onCancel
}) => {
  const { t } = useTranslation();
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
        <div className="p-6">
          <ReceiptBinWarningPanel binCodes={binCodes} />
        </div>
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-lg flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            {t('common.buttons.cancel', {}, 'Avbryt')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="px-4 py-2 text-sm font-medium bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors"
          >
            {t('manager.modules.adminItemsLocations.receiptBinWarning.saveAnyway', {}, 'Lagre likevel')}
          </button>
        </div>
      </div>
    </div>
  );
};
